import { useState } from 'react';
import { Alert, Button, Col, Empty, Flex, Input, Row, Skeleton, Typography } from 'antd';
import { RiSearchLine } from 'react-icons/ri';
import { useNavigate } from 'react-router';

import { useCollections } from '@hooks/useCollections';
import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { useProject } from '@contexts/Project.context';
import { COLLECTION_TYPE } from '@utils/constants';

import CollectionCard from './CollectionCard';

const SectionHeading = ({ label, hint }) => (
	<Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: 1.5 }}>
		<span style={{ fontWeight: 700 }}>{label}</span>
		{hint ? <span style={{ fontWeight: 400 }}> {hint}</span> : null}
	</Typography.Text>
);

/** A section keeps its heading during a search, so the layout stays stable. */
const NoSectionMatch = () => (
	<Typography.Text type="secondary" style={{ fontSize: 12 }}>
		No matches in this section.
	</Typography.Text>
);

/** A heading plus its cards; a search can empty the section but keeps it. */
const CollectionSection = ({ label, hint, collections, selectedId, onSelect }) => (
	<Flex vertical gap={12}>
		<SectionHeading label={label} hint={hint} />
		{collections.length === 0 ? (
			<NoSectionMatch />
		) : (
			<Row gutter={[16, 16]}>
				{collections.map(collection => (
					<Col xs={24} md={12} key={collection.id}>
						<CollectionCard
							collection={collection}
							onSelect={onSelect}
							selected={collection.id === selectedId}
						/>
					</Col>
				))}
			</Row>
		)}
	</Flex>
);

/**
 * A collection matches when its name contains the query, or when any of its
 * objects does — a search for "pine" finds every collection that holds Pine.
 */
const matchesQuery = (collection, query) => {
	if (collection.name?.toLowerCase().includes(query)) return true;
	return Boolean(collection.objects?.some(object => object.label?.toLowerCase().includes(query)));
};

/**
 * Collection step of the detection wizard: choose the saved collection to run.
 * Choosing one advances the wizard to the Area step.
 *
 * Only collections that can run on this project are offered — organization and
 * personal ones of the project's type. Templates are never run directly, and
 * collections are created and managed on the Collections page, not here.
 */
const Collections = () => {
	const { data: collections = [], isLoading, isError } = useCollections();
	const { projectMode } = useProject();
	const navigate = useNavigate();

	const sourceCollectionId = useObjectPickerStore(s => s.sourceCollectionId);
	const selectCollection = useObjectPickerStore(s => s.selectCollection);
	const nextStep = useObjectPickerStore(s => s.nextStep);
	const closeAndReset = useObjectPickerStore(s => s.closeAndReset);

	const [searchQuery, setSearchQuery] = useState('');
	const trimmedQuery = searchQuery.trim().toLowerCase();
	const isSearching = trimmedQuery.length > 0;

	const ofProjectType = collections.filter(collection => collection.mode === projectMode);
	const allOrg = ofProjectType.filter(collection => collection.type === COLLECTION_TYPE.ORG);
	const allPersonal = ofProjectType.filter(
		collection => collection.type === COLLECTION_TYPE.PERSONAL
	);

	// A section shows whenever its kind exists; a search only empties it.
	const bySearch = list =>
		isSearching ? list.filter(collection => matchesQuery(collection, trimmedQuery)) : list;
	const orgCollections = bySearch(allOrg);
	const personal = bySearch(allPersonal);

	const chooseCollection = collection => {
		selectCollection(collection);
		nextStep();
	};

	const goToCollectionsPage = () => {
		closeAndReset();
		navigate(`/collections?tab=${projectMode}`);
	};

	const renderBody = () => {
		if (isLoading) {
			return <Skeleton active paragraph={{ rows: 8 }} title={false} />;
		}

		if (isError) {
			return (
				<Alert
					type="error"
					showIcon
					message="Couldn't load collections"
					description="Please close this and try again."
				/>
			);
		}

		if (allOrg.length + allPersonal.length === 0) {
			return (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="No collections for this project type yet. Create one on the Collections page.">
					<Button type="primary" onClick={goToCollectionsPage}>
						Go to Collections
					</Button>
				</Empty>
			);
		}

		if (orgCollections.length + personal.length === 0) {
			return (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={
						<Typography.Text type="secondary">
							No collections match &ldquo;{searchQuery.trim()}&rdquo;
						</Typography.Text>
					}
				/>
			);
		}

		return (
			<Flex vertical gap={24}>
				{allOrg.length > 0 && (
					<CollectionSection
						label="ORGANIZATION"
						hint="shared across your organization"
						collections={orgCollections}
						selectedId={sourceCollectionId}
						onSelect={chooseCollection}
					/>
				)}
				{allPersonal.length > 0 && (
					<CollectionSection
						label="MY COLLECTIONS"
						collections={personal}
						selectedId={sourceCollectionId}
						onSelect={chooseCollection}
					/>
				)}
			</Flex>
		);
	};

	return (
		<Flex vertical gap={12} style={{ height: '60vh' }}>
			<Input
				aria-label="Search collections or objects"
				placeholder="Search collections or objects — gran, ungskogpleie..."
				prefix={<RiSearchLine />}
				allowClear
				value={searchQuery}
				onChange={event => setSearchQuery(event.target.value)}
			/>
			<div
				// antd's Row gutter adds negative side margins; hide them rather than
				// show a sideways scrollbar.
				style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
				data-tour="picker-collection">
				{renderBody()}
			</div>
		</Flex>
	);
};

export default Collections;
